import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChange, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BuildingsStoreService } from '@app/energy-map/services/buildings-store.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ConsumersService } from '@app/shared/services/api/consumers.service';
import { ProducersService } from '@app/shared/services/api/producer.service';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { cssVariables } from '@env/css-vars';
import { TranslateService } from '@ngx-translate/core';
import { assign, chain, filter, flatMap, isEqual, isNil, last, map, set } from 'lodash';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-description',
  templateUrl: './description.component.html',
  styleUrls: ['./description.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DescriptionComponent implements OnChanges, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() assets: any[] = [];
  @Input() IsEditBuilding = false;
  @Output() closeEvent = new EventEmitter();
  @Output() saveEmitter = new EventEmitter();
  @Output() addBuildingEmitter = new EventEmitter();
  buildingAssets: any | null = null;
  data: any | null = null;
  editType = 1;
  dataFromDialog = null;
  building = null;
  consumers: any[] = [];
  producers: any[] = [];
  consumersFromDialog = [];
  producersFromDialog = [];
  plantType = null;
  plantTypeFromDialog = null;
  filteredProducers: any[] = [];
  stateType: string[] = ['consumer', 'producer', 'asset-list', 'producer-list'];
  state: any[] = [];
  isBenchmarkVisible = false;
  isWordingVisible = false;
  isProducerBenchmarkVisible = false;
  isQuestionnaireVisible = false;
  private _isModalVisible: boolean = false;
  isEditMode = false;
  plantTypes: any[] = [];
  plantTypesFromDialog = [];
  sensors: any[] = [];
  hasEfficiencyAnalysis = false;
  closeEaWizard = false;
  isFromDescriptionConsumer = false;
  isEfficiencyAnalysisOpen = false;

  constructor(
    private buildingStoreService: BuildingsStoreService,
    private consumersService: ConsumersService,
    private efficiencyAnalysisService: EfficiencyAnalysisService,
    private matDialog: MatDialog,
    private overlayLoaderService: OverlayLoaderService,
    private producersService: ProducersService,
    private translate: TranslateService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  @Input()
  set isModalVisible(isModalVisible: boolean) {
    this._isModalVisible = isModalVisible;
  }

  get isModalVisible(): boolean {
    return this._isModalVisible;
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.assets = [];
    this.sensors = [];
    this.isModalVisible = false;
  }

  hideVisibility() {
    this.initializeComponentData();
    this.isModalVisible = false;
    this.closeEvent.emit(true);
    this.buildingStoreService.selectedBuilding = null;
  }

  descriptionConsumerClose(building: any) {
    this.hideVisibility();

    if (!isNil(building)) {
      this.buildingStoreService.selectedBuildingWithUnkownTypeOrGps = building;
    }
  }

  public updateAsset(data: any) {
    this.assets = data;
  }

  initializeComponentData() {
    this.buildingAssets = null;
    this.data = null;
    this.editType = 1;
    this.dataFromDialog = null;
    this.building = null;
    this.consumers = [];
    this.producers = [];
    this.consumersFromDialog = [];
    this.producersFromDialog = [];
    this.filteredProducers = [];
    this.state = [];
    this.isBenchmarkVisible = false;
    this.isWordingVisible = false;
    this.isProducerBenchmarkVisible = false;
    this.isQuestionnaireVisible = false;
    this.isEditMode = false;
    this.sensors = [];
  }

  save(updatedData: any) {
    this.overlayLoaderService.isLoading = true;
    if (updatedData.type === 'consumer') {
      const newConsumer = {
        guid: updatedData.guid,
        name: updatedData.name,
        buildingTypeId: !isNil(updatedData.buildingType) ? updatedData.buildingType.buildingTypeId : null,
        description: updatedData.description,
        floorArea: updatedData.floorArea,
        municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
      };
      this.consumersService
        .updateBuilding(newConsumer)
        .pipe(first())
        .subscribe(() => {
          set(
            updatedData,
            'marker.options.data',
            assign(updatedData.marker.options.data, newConsumer, {
              buildingType: !isNil(updatedData.buildingType) ? updatedData.buildingType : null,
            })
          );
          if (!this.isFromDescriptionConsumer) {
            this.overlayLoaderService.isLoading = false;
          }
        });
    } else if (updatedData.type === 'producer') {
      const newProducer = {
        name: updatedData.name,
        unitPerformance: updatedData.unitPerformance,
        description: updatedData.description,
      };
      this.producersService
        .updateDetails(updatedData.assetId, newProducer)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          set(updatedData, 'marker.options.data', assign(updatedData.marker.options.data, newProducer));
          this.overlayLoaderService.isLoading = false;
        });
    }
    this.saveEmitter.emit(updatedData);
  }

  saveEfficiencyAnalysis(updatedData: any) {
    this.overlayLoaderService.isLoading = true;
    const newConsumer = {
      guid: updatedData.guid,

      floorArea: updatedData.floorArea,

      electricityConsumption: updatedData.electricityConsumption,
      heatingOilConsumption: updatedData.heatingOilConsumption,
      naturalGasConsumption: updatedData.naturalGasConsumption,
      lpgConsumption: updatedData.lpgConsumption,
      districtHeatingConsumption: updatedData.districtHeatingConsumption,
      electricityHeatConsumption: updatedData.electricityHeatConsumption,
      pelletWoodConsumption: updatedData.pelletWoodConsumption,

      electricityFromDate: updatedData.electricityFromDate,
      heatingOilFromDate: updatedData.heatingOilFromDate,
      naturalGasFromDate: updatedData.naturalGasFromDate,
      lpgFromDate: updatedData.lpgFromDate,
      districtHeatingFromDate: updatedData.districtHeatingFromDate,
      electricityHeatFromDate: updatedData.electricityHeatFromDate,
      pelletWoodFromDate: updatedData.pelletWoodFromDate,

      electricityToDate: updatedData.electricityToDate,
      heatingOilToDate: updatedData.heatingOilToDate,
      naturalGasToDate: updatedData.naturalGasToDate,
      lpgToDate: updatedData.lpgToDate,
      districtHeatingToDate: updatedData.districtHeatingToDate,
      electricityHeatToDate: updatedData.electricityHeatToDate,
      pelletWoodToDate: updatedData.pelletWoodToDate,

      electricityComputedValue: updatedData.electricityComputedValue,
      electricityColorStatus: updatedData.electricityColorStatus,
      averageElectricityConsumption: updatedData.averageElectricityConsumption,
      electricityGuideValue: updatedData.electricityGuideValue,
      electricityMinDataRange: updatedData.electricityMinDataRange,
      electricityMaxDataRange: updatedData.electricityMaxDataRange,
      efficiencyPercentageElectricity: updatedData.efficiencyPercentageElectricity,

      heatComputedValue: updatedData.heatComputedValue,
      heatColorStatus: updatedData.heatColorStatus,
      averageHeatConsumption: updatedData.averageHeatConsumption,
      heatGuideValue: updatedData.heatGuideValue,
      heatMinDataRange: updatedData.heatMinDataRange,
      heatMaxDataRange: updatedData.heatMaxDataRange,
      efficiencyPercentageHeat: updatedData.efficiencyPercentageHeat,

      efficiencyAnalysisDate: updatedData.efficiencyAnalysisDate,
      notes: updatedData.notes,

      municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
    };

    this.consumersService
      .updateBuilding(newConsumer)
      .pipe(first())
      .subscribe(() => {
        set(
          updatedData,
          'marker.options.data',
          assign(updatedData.marker.options.data, newConsumer, {
            buildingType: !isNil(updatedData.buildingType) ? updatedData.buildingType : null,
          })
        );
        this.overlayLoaderService.isLoading = false;
        this.matDialog
          .open(NotificationDialogComponent, {
            data: {
              descriptionText: this.translate.instant('module.efficiencyAnalysis.saveSuccessText'),
              descriptionFaClass: 'fa-check-circle fa-2x',
              descriptionFaColor: cssVariables['--success-color'],
              confirmText: 'Ok',
            },
            panelClass: ['-md'],
          })
          .afterClosed()
          .subscribe(() => {
            this.saveEmitter.emit(updatedData);
            this.closeWordingModal();
          });
      });
  }

  saveNewEfficiencyAnalysis(data: any) {
    const marker = this.assets[0];
    Object.assign(marker, data.buildingEfficiencyData);
    set(marker, 'marker.options.data', assign(marker.marker.options.data, data.buildingEfficiencyData));
    this.saveEmitter.emit(marker);
    const matRef = this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: this.translate.instant('module.efficiencyAnalysis.saveSuccessText'),
        descriptionFaClass: 'fa-check-circle fa-2x',
        descriptionFaColor: cssVariables['--confirm-success-color'],
        hideClose: true,
      },
      panelClass: ['-md'],
    });

    matRef.afterClosed().subscribe(() => {
      this.closeWordingModal();
    });
  }

  setType(buildingType: any) {
    this.data.buildingType = {
      ...this.data.buildingType,
      ...buildingType,
    };
  }

  closeEfficiencyAnalysisWizard(wordingVisibility?: boolean) {
    this.isEfficiencyAnalysisOpen = false;
  }

  openEfficiencyWizard = () => {
    this.isEfficiencyAnalysisOpen = true;
  };

  closeWordingModal() {
    this.isWordingVisible = false;
    this.hideVisibility();
  }

  closeProcedureBenchmarkingModal() {
    this.isProducerBenchmarkVisible = false;
  }

  onQuestionnaireClose(data: any) {
    this.isQuestionnaireVisible = false;
    if (data.next) {
      if (!isEqual(data.inclination, this.data.inclination) || !isEqual(data.orientation, this.data.orientation)) {
        this.producersService.updatePerformance(this.data.assetId, data).pipe(takeUntil(this.unsubscribe$)).subscribe();
      }

      this.isModalVisible = false;
      this.isProducerBenchmarkVisible = true;
    } else {
      this.hideVisibility();
    }
  }

  public ngOnChanges(changes: { [propKey: string]: SimpleChange }): void {
    for (const propName in changes) {
      if (propName && propName === 'assets' && !isEqual(changes[propName].currentValue, changes[propName].previousValue)) {
        this.state = [];
        this.assets = changes[propName].currentValue;
        this.assets = map(this.assets, (a) => assign({}, a));

        this.buildingAssets = isNil(this.assets[0].building) ? this.assets[0] : this.assets[0].building;
        this.consumers = isNil(this.assets[0].building)
          ? filter(this.assets, (asset) => asset.type === 'consumer')
          : filter(this.assets, (asset) => asset.building.type === 'consumer');
        this.producers = isNil(this.assets[0].building)
          ? filter(this.assets, (asset) => asset.type === 'producer')
          : filter(this.assets, (asset) => asset.building.type === 'producer');
        this.assignSensors();
        this.getPlantTypes();

        if (this.assets.length > 1) {
          if (this.consumers.length > 0 || this.plantTypes.length > 1) {
            this.state.push(this.stateType[2]);
          } else {
            this.selectPlantType(this.plantTypes[0]);
          }
        } else if (this.buildingAssets.type === 'consumer') {
          this.data = this.buildingAssets;
          this.buildingStoreService.setSelectedBuildingStore(this.data);
          this.editType = isNil(this.assets[0].editType) ? 1 : this.assets[0].editType;
          this.state.push(this.stateType[0]);
        } else if (this.buildingAssets.type === 'producer') {
          this.data = this.buildingAssets;
          this.state.push(this.stateType[1]);
        }

        const buildingId =
          isNil(this.assets[0].buildingType) || isNil(this.assets[0].buildingType.guid) ? this.assets[0].buildingTypeId : this.assets[0].buildingType.guid;
        this.efficiencyAnalysisService.setEfficiencyAnalysisStateByGuid(buildingId);

        if (this.isBenchmarkVisible || this.isWordingVisible) {
          this.closeEfficiencyAnalysisWizard(false);
          this.isBenchmarkVisible = false;
          this.isModalVisible = false;
          this.isWordingVisible = false;
        }
      }
    }
  }

  public selectAsset(asset: any) {
    this.data = asset;
    if (this.data.type === 'consumer') {
      this.state.push(this.stateType[0]);
    } else if (this.data.type === 'producer') {
      this.state.push(this.stateType[1]);
    }
  }

  public selectPlantType(type: any) {
    this.plantType = type;

    this.filterProducers();

    if (this.filteredProducers.length === 1) {
      this.data = this.filteredProducers[0];
      this.state.push(this.stateType[1]);
    } else {
      this.state.push(this.stateType[3]);
    }
  }

  public filterProducers() {
    this.filteredProducers = chain(this.producers).filter({ plantType: this.plantType }).value();
  }

  public back() {
    if (this.state.length > 1) {
      this.buildingStoreService.selectedBuilding = null;
      this.state.pop();
    }
  }

  public getPlantTypes() {
    this.plantTypes = chain(this.producers)
      .uniqBy('plantType')
      .map((consumer: any) => consumer.plantType)
      .value();
  }

  getLast(collection: any[]): string {
    return last(collection);
  }

  addBuilding(data: any) {
    this.addBuildingEmitter.emit(data);
  }

  onUpdateNote(building: any) {
    const newConsumer = {
      guid: building.guid,
      notes: building.notes,
      municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
    };

    this.consumersService
      .updateBuilding(newConsumer)
      .pipe(first())
      .subscribe(() => {
        this.isBenchmarkVisible = true;
        this.isWordingVisible = false;

        set(
          building,
          'marker.options.data',
          assign(building.marker.options.data, newConsumer, {
            buildingType: !isNil(building.buildingType) ? building.buildingType : null,
          })
        );

        this.saveEmitter.emit(building);

        if (this.closeEaWizard) {
          this.hideVisibility();
          this.closeEaWizard = false;
        }
      });
  }

  private assignSensors(): void {
    this.sensors = [];
    const assets = this.assets[0][0] ? flatMap(this.assets[0]) : this.assets;
    if (assets.every((a) => a.device_id)) {
      this.sensors = assets;
    }
  }
}
